/*
DashboardSidebar Attribution: https://github.com/mui/material-ui/tree/v7.3.4/docs/data/material/getting-started/templates/crud-dashboard
*/
import * as React from "react";
import { useTransition } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Toolbar from "@mui/material/Toolbar";

import HomeIcon from "@mui/icons-material/Home";

import { matchPath, useLocation } from "react-router";
import DashboardSidebarContext, {
  DashboardSidebarContextValue,
} from "./DashboardSidebarContext";
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from "./constants";
import DashboardSidebarPageItem from "./DashboardSidebarPageItem";

import DashboardSidebarDividerItem from "./DashboardSidebarDividerItem";
import {
  getDrawerSxTransitionMixin,
  getDrawerWidthTransitionMixin,
} from "./mixins";
import { SignInButton } from "../SignInButton";
import { SignOutButton } from "../SignOutButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import TransportationIcon from "@mui/icons-material/EmojiTransportation";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BuildIcon from "@mui/icons-material/Build";
import LicensePlateIcon from "@/components/Icons/LicensePlateIcon";
import { useCurrentUser } from "@/hooks/useProfileData";
import { useSelectedAccount } from "@/contexts/SelectedAccountContext";
import { msalInstance } from "@/index";
import {
  requiresCompanySetup,
  getIdentityCompanyIdFromClaims,
} from "@/utils/companySetup";
import { IdTokenClaims } from "@/types/auth.types";
import { hasEstablishedDynamicsContact } from "@/utils/dynamicsContact";

interface DashboardSidebarProps {
  expanded?: boolean;
  setExpanded: (expanded: boolean) => void;
  disableCollapsibleSidebar?: boolean;
  container?: any;
  isAuthenticated?: boolean;
}

function DashboardSidebar({
  expanded = true,
  setExpanded,
  disableCollapsibleSidebar = false,
  container,
  isAuthenticated = false,
}: DashboardSidebarProps) {
  const normalizeOrganizationId = (id?: string | null) =>
    (id ?? "").trim().replace(/["{}]/g, "").toLowerCase();

  const theme = useTheme();

  const { pathname } = useLocation();

  const { data: dynamicsUser } = useCurrentUser();
  const { selectedAccountId, setSelectedOrganizationId } = useSelectedAccount();
  const currentAccount = msalInstance.getActiveAccount();
  const jobTitle = currentAccount?.idTokenClaims?.JobTitle as
    | string
    | undefined;
  const identityClaims = currentAccount?.idTokenClaims as
    | IdTokenClaims
    | undefined;
  const businessId = getIdentityCompanyIdFromClaims(identityClaims);
  const organizations = dynamicsUser?.organizations ?? [];

  const singleOrganizationAccountId =
    organizations.length === 1 ? organizations[0]?.account?.id : undefined;
  const hasSelectedCompany = organizations.some(
    (organization) =>
      normalizeOrganizationId(organization.account.id) ===
      normalizeOrganizationId(selectedAccountId),
  );
  const effectiveSelectedAccountId =
    selectedAccountId && hasSelectedCompany
      ? selectedAccountId
      : (singleOrganizationAccountId ?? null);
  const requiresSetup = requiresCompanySetup({
    jobTitle,
    businessId,
    contactData: dynamicsUser,
  });
  const isCompanySetupMode = requiresSetup;
  const hasEstablishedContact = hasEstablishedDynamicsContact(dynamicsUser);

  React.useEffect(() => {
    if (!singleOrganizationAccountId) {
      return;
    }

    if (
      normalizeOrganizationId(selectedAccountId) !==
      normalizeOrganizationId(singleOrganizationAccountId)
    ) {
      setSelectedOrganizationId(singleOrganizationAccountId);
    }
  }, [
    selectedAccountId,
    setSelectedOrganizationId,
    singleOrganizationAccountId,
  ]);

  const isOverSmViewport = useMediaQuery(theme.breakpoints.up("sm"));
  const isOverMdViewport = useMediaQuery(theme.breakpoints.up("md"));

  const [isFullyExpanded, setIsFullyExpanded] = React.useState(expanded);
  const [isFullyCollapsed, setIsFullyCollapsed] = React.useState(!expanded);
  const [_isPending, startTransition] = useTransition();
  const authActionRef = React.useRef<HTMLElement | null>(null);
  const handleSetAuthActionRef = React.useCallback(
    (node: HTMLElement | null) => {
      authActionRef.current = node ?? null;
    },
    [],
  );

  React.useEffect(() => {
    if (expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        startTransition(() => setIsFullyExpanded(true));
      }, theme.transitions.duration.enteringScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    startTransition(() => setIsFullyExpanded(false));

    return () => {};
  }, [expanded, theme.transitions.duration.enteringScreen, startTransition]);

  React.useEffect(() => {
    if (!expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        startTransition(() => setIsFullyCollapsed(true));
      }, theme.transitions.duration.leavingScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    startTransition(() => setIsFullyCollapsed(false));

    return () => {};
  }, [expanded, theme.transitions.duration.leavingScreen, startTransition]);

  const mini = !disableCollapsibleSidebar && !expanded;

  const handleSetSidebarExpanded = React.useCallback(
    (newExpanded: boolean) => () => {
      setExpanded(newExpanded);
    },
    [setExpanded],
  );

  const handleAuthActionClick = React.useCallback(() => {
    const button = authActionRef.current?.querySelector?.("button");
    (button as HTMLButtonElement | undefined)?.click?.();
  }, []);

  const handlePageItemClick = React.useCallback(
    (itemId: string, hasNestedNavigation: boolean) => {
      if (itemId === "auth") {
        handleAuthActionClick();
      }
      if (!isOverSmViewport && !hasNestedNavigation) {
        setExpanded(false);
      }
    },
    [setExpanded, isOverSmViewport, handleAuthActionClick],
  );

  const hasDrawerTransitions =
    isOverSmViewport && (!disableCollapsibleSidebar || isOverMdViewport);

  const shouldRenderTemporaryDrawer =
    !isOverSmViewport || disableCollapsibleSidebar;
  const isTemporaryDrawerOpen = shouldRenderTemporaryDrawer && expanded;

  const getDrawerContent = React.useCallback(
    (viewport: string) => (
      <React.Fragment>
        <Toolbar />
        <Box
          component="nav"
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflowY: "auto",
            overflowX: "clip",
            scrollbarGutter: mini ? "stable" : "auto",
            pt: !mini ? 0 : 2,
            ...(hasDrawerTransitions
              ? getDrawerSxTransitionMixin(isFullyExpanded, "padding")
              : {}),
          }}
        >
          <List
            dense
            sx={{
              padding: mini ? 0 : "4px 0",
              mb: 4,
              width: mini ? MINI_DRAWER_WIDTH : "100%",
              overflow: "visible",
            }}
          >
            {isAuthenticated && dynamicsUser !== undefined ? (
              <React.Fragment>
                <DashboardSidebarPageItem
                  id="home"
                  title="Home"
                  icon={<HomeIcon />}
                  href="/home"
                  selected={!!matchPath("/home", pathname)}
                />
                <DashboardSidebarDividerItem />
                {(() => {
                  const isMechanicWithoutOrganization = Boolean(
                    dynamicsUser?.mechanicIds?.length &&
                    !effectiveSelectedAccountId,
                  );

                  // Show limited menu items for company setup mode
                  if (isCompanySetupMode) {
                    return (
                      <>
                        <DashboardSidebarPageItem
                          id="company"
                          title="Company Information"
                          icon={<BusinessIcon />}
                          href="/company"
                          selected={!!matchPath("/company", pathname)}
                        />
                        {hasEstablishedContact ? (
                          <DashboardSidebarPageItem
                            id="paymenthistory"
                            title="My Payment History"
                            icon={<AttachMoneyIcon />}
                            href="/paymenthistory"
                            selected={!!matchPath("/paymenthistory", pathname)}
                          />
                        ) : null}
                      </>
                    );
                  }

                  // Show only mechanics link for mechanics without selected company
                  if (isMechanicWithoutOrganization) {
                    return (
                      <>
                        <DashboardSidebarPageItem
                          id="mechanics"
                          title="My Mechanic Details"
                          icon={<BuildIcon sx={{ fontSize: 20 }} />}
                          href="/mechanics/me"
                          selected={!!matchPath("/mechanics/me", pathname)}
                        />
                        <DashboardSidebarPageItem
                          id="plates"
                          title="Plates"
                          icon={
                            <LicensePlateIcon
                              style={{ fontSize: 20, color: "currentColor" }}
                            />
                          }
                          href="/plates"
                          selected={
                            !!matchPath("/plates", pathname) ||
                            !!matchPath("/plates/*", pathname)
                          }
                        />
                      </>
                    );
                  }

                  // Show normal menu items for users with selected company or specific user types
                  return (
                    <>
                      {(effectiveSelectedAccountId ||
                        ["1", "2"].includes(
                          String(dynamicsUser?.identity?.userType ?? ""),
                        )) && (
                        <DashboardSidebarPageItem
                          id="company"
                          title="Company Information"
                          icon={<BusinessIcon />}
                          href="/company"
                          selected={!!matchPath("/company", pathname)}
                        />
                      )}

                      {effectiveSelectedAccountId ? (
                        <>
                          <DashboardSidebarPageItem
                            id="brokeragestncs"
                            title="Brokerages and TNCs"
                            icon={<TransportationIcon />}
                            href="/brokeragestncs"
                            selected={!!matchPath("/brokeragestncs", pathname)}
                          />

                          <DashboardSidebarPageItem
                            id="inspectionstations"
                            title="Inspection Stations"
                            icon={<CarRepairIcon />}
                            href="/inspectionstations"
                            selected={
                              !!matchPath("/inspectionstations", pathname)
                            }
                          />
                          <DashboardSidebarPageItem
                            id="mechanics"
                            title="Mechanics"
                            icon={<BuildIcon sx={{ fontSize: 20 }} />}
                            href="/mechanics"
                            selected={!!matchPath("/mechanics", pathname)}
                          />
                        </>
                      ) : null}
                      <DashboardSidebarPageItem
                        id="plates"
                        title="Plates"
                        icon={
                          <LicensePlateIcon
                            style={{ fontSize: 20, color: "currentColor" }}
                          />
                        }
                        href="/plates"
                        selected={
                          !!matchPath("/plates", pathname) ||
                          !!matchPath("/plates/*", pathname)
                        }
                      />
                    </>
                  );
                })()}
                {!isCompanySetupMode && hasEstablishedContact && (
                  <DashboardSidebarPageItem
                    id="paymenthistory"
                    title="My Payment History"
                    icon={<AttachMoneyIcon />}
                    href="/paymenthistory"
                    selected={!!matchPath("/paymenthistory", pathname)}
                  />
                )}

                <DashboardSidebarPageItem
                  id="auth"
                  title={mini ? (isAuthenticated ? "Sign Out" : "Sign In") : ""}
                  icon={
                    <React.Fragment>
                      <LockOutlinedIcon
                        onClick={handleAuthActionClick}
                        sx={{ cursor: "pointer" }}
                      />
                      {mini ? (
                        // Render a hidden auth button in mini mode so the lock icon can always forward clicks to the correct sign in/out action.
                        // This ensures the ref points to a button even when the visible button is not rendered in the sidebar.
                        <Box
                          ref={handleSetAuthActionRef}
                          sx={{ display: "none" }}
                        >
                          {isAuthenticated ? (
                            <SignOutButton />
                          ) : (
                            <SignInButton />
                          )}
                        </Box>
                      ) : null}
                    </React.Fragment>
                  }
                  href={isAuthenticated ? "/signout" : "/signin"}
                  action={
                    <Box
                      ref={handleSetAuthActionRef}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      {isAuthenticated ? <SignOutButton /> : <SignInButton />}
                    </Box>
                  }
                />
              </React.Fragment>
            ) : null}
          </List>
        </Box>
      </React.Fragment>
    ),
    [
      mini,
      hasDrawerTransitions,
      isFullyExpanded,
      isAuthenticated,
      dynamicsUser,
      effectiveSelectedAccountId,
      pathname,
      handleAuthActionClick,
      handleSetAuthActionRef,
      isCompanySetupMode,
      hasEstablishedContact,
    ],
  );

  // Summary:
  // - Temporary drawers (overlays) need only basic sizing and background.
  // - Permanent drawers (side panels) need extra flex, sizing, and overflow rules to integrate with the page layout and content.
  const getDrawerSharedSx = React.useCallback(
    (isTemporary: boolean) => {
      const drawerWidth = mini ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

      if (isTemporary) {
        return {
          displayPrint: "none",
          width: drawerWidth,
          flexShrink: 0,
          ...getDrawerWidthTransitionMixin(expanded),
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundImage: "none",
            ...getDrawerWidthTransitionMixin(expanded),
          },
        };
      }

      return {
        displayPrint: "none",
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(expanded),
        ...(isTemporary ? { position: "absolute" } : {}),
        [`& .MuiDrawer-paper`]: {
          position: "absolute",
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundImage: "none",
          ...getDrawerWidthTransitionMixin(expanded),
        },
      };
    },
    [expanded, mini],
  );

  const temporaryDrawerSx = getDrawerSharedSx(true);

  const permanentDrawerSx = getDrawerSharedSx(false);

  const sidebarContextValue: DashboardSidebarContextValue = {
    onPageItemClick: handlePageItemClick,
    mini,
    fullyExpanded: isFullyExpanded,
    fullyCollapsed: isFullyCollapsed,
    hasDrawerTransitions,
  };

  return (
    <DashboardSidebarContext.Provider value={sidebarContextValue}>
      {shouldRenderTemporaryDrawer ? (
        <Drawer
          container={container}
          variant="temporary"
          open={isTemporaryDrawerOpen}
          onClose={handleSetSidebarExpanded(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          slotProps={{
            paper: {
              "aria-label": "Main navigation menu",
            },
          }}
          sx={
            {
              display: {
                xs: "block",
                sm: disableCollapsibleSidebar ? "block" : "none",
                md: "none",
              },
              ...temporaryDrawerSx,
            } as any
          }
        >
          {getDrawerContent("phone")}
        </Drawer>
      ) : null}
      <Drawer
        variant="permanent"
        sx={
          {
            display: {
              xs: "none",
              sm: disableCollapsibleSidebar ? "none" : "block",
              md: "none",
            },
            ...permanentDrawerSx,
            "& .MuiDrawer-paper": {
              borderBottom: (theme: any) =>
                `1px solid ${theme.palette.divider}`,
              ...permanentDrawerSx["& .MuiDrawer-paper"],
            },
          } as any
        }
      >
        {getDrawerContent("tablet")}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={
          {
            display: { xs: "none", md: "block" },
            ...permanentDrawerSx,
            "& .MuiDrawer-paper": {
              borderBottom: (theme: any) =>
                `1px solid ${theme.palette.divider}`,
              ...permanentDrawerSx["& .MuiDrawer-paper"],
            },
          } as any
        }
      >
        {getDrawerContent("desktop")}
      </Drawer>
    </DashboardSidebarContext.Provider>
  );
}

export default DashboardSidebar;
